// src/pages/Chat/ConversationList.jsx
//
// Sidebar: loads conversations from REST API on mount, populates chatStore,
// then renders ConversationItem for each one.
//
// THIS WAS THE MISSING FILE — nothing was ever fetching conversations or
// putting them into the store, so the sidebar always showed empty / stale data.

import React, { useEffect, useState, useCallback, useRef } from "react";
import { FiRefreshCw, FiEdit, FiSearch, FiLoader, FiUsers } from "react-icons/fi";
import { resolvePhotoUrl } from "../../utils/photoHelper";
import useChatStore from "../../store/chatStore";
import useAuthStore from "../../store/authStore";
import { getConversations, setPresenceStatus, getPresence } from "../../api/chatService";
import ConversationItem from "./ConversationItem";
import NewDMModal from "./NewDMModal";
import NewGroupModal from "./NewGroupModal";
import UserProfileModal from "./UserProfileModal";
import { FiChevronDown } from "react-icons/fi";

function CurrentUserAvatar({ resolvedUserId, onShowProfile }) {
  const [imgErr, setImgErr] = useState(false);
  const user = useAuthStore((state) => state.user) || {};
  const profilePhoto = useAuthStore((state) => state.profilePhoto);
  const photoUrl = resolvePhotoUrl(profilePhoto);
  const initials = (user.full_name || "Y").split(" ").map(n => n[0]).join("").toUpperCase();

  return (
    <button
      onClick={() => onShowProfile({
        employee_id: resolvedUserId,
        full_name: user.full_name || "You",
        email: user.email,
        role: user.role,
        department: user.department || user.dept,
        photo: profilePhoto,
      })}
      className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[10px] font-bold shadow-lg hover:scale-110 transition-transform overflow-hidden"
    >
      {photoUrl && !imgErr ? (
        <img
          src={photoUrl}
          alt="You"
          className="w-full h-full object-cover rounded-full"
          onError={() => setImgErr(true)}
        />
      ) : (
        initials
      )}
    </button>
  );
}

export default function ConversationList({ currentUserId }) {
  const conversations   = useChatStore((s) => s.conversations);
  const setConversations = useChatStore((s) => s.setConversations);
  const wsConnected      = useChatStore((s) => s.wsConnected);
  const bulkSetPresence  = useChatStore((s) => s.bulkSetPresence);

  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const [search,        setSearch]        = useState("");
  const [showNewDM,     setShowNewDM]     = useState(false);
  const [showNewGroup,  setShowNewGroup]  = useState(false);
  const [profileTarget, setProfileTarget] = useState(null);
  const [updatingPresence, setUpdatingPresence] = useState(false);

  const fetchedIdsRef = useRef(new Set());

  // Resolve currentUserId from store if not passed as prop
  const currentUserIdFromStore = useChatStore((s) => s.currentUserId);
  const resolvedUserId = currentUserId || currentUserIdFromStore;
  
  // Real presence from store (falls back to wsConnected if not yet fetched)
  const myPresenceStatus = useChatStore((s) => 
    resolvedUserId ? s.presence[resolvedUserId] : undefined
  );
  
  const isOnline = myPresenceStatus ? myPresenceStatus === "ONLINE" : wsConnected;

  const togglePresence = async () => {
    if (updatingPresence) return;
    setUpdatingPresence(true);
    try {
      const newStatus = isOnline ? "OFFLINE" : "ONLINE";
      await setPresenceStatus(newStatus);
      // Optimistically update the store
      if (resolvedUserId) {
        useChatStore.getState().setPresence(resolvedUserId, newStatus);
      }
    } catch (err) {
      console.error("Failed to toggle presence:", err);
    } finally {
      setUpdatingPresence(false);
    }
  };

  // ── Load conversations from REST API ───────────────────────────────────────
  const loadConversations = useCallback(async () => {
    setLoading(true);
    setError(null);
    fetchedIdsRef.current.clear();
    try {
      const { data } = await getConversations();
      // API returns either an array or { results: [...] } (pagination)
      const list = Array.isArray(data) ? data : (data.results || data.conversations || []);
      setConversations(list);
    } catch (err) {
      console.error("[ConversationList] failed to load:", err);
      setError("Failed to load conversations.");
    } finally {
      setLoading(false);
    }
  }, [setConversations]);

  const selectedConvId = useChatStore((s) => s.selectedConvId);

  useEffect(() => {
    setProfileTarget(null);
  }, [selectedConvId]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // ── Bulk-fetch presence status for conversation members ─────────────────────
  useEffect(() => {
    if (!conversations.length) return;
    const ids = [];
    conversations.forEach((c) => {
      (c.members || []).forEach((m) => {
        const eid = m.employee_id || m.user?.employee_id;
        if (eid && eid !== resolvedUserId && !fetchedIdsRef.current.has(eid)) {
          ids.push(eid);
        }
      });
    });
    if (ids.length === 0) return;

    // Proactively mark IDs as fetched to prevent redundant API calls during rapid state transitions
    ids.forEach((id) => fetchedIdsRef.current.add(id));

    getPresence(ids)
      .then(({ data }) => {
        const map = {};
        const presenceData = data.presence || {};
        Object.entries(presenceData).forEach(([eid, info]) => {
          map[eid] = info.status || "OFFLINE";
        });
        bulkSetPresence(map);
      })
      .catch((err) => {
        console.error("[ConversationList] failed to fetch presence status:", err);
        // Remove from ref on error to allow retry
        ids.forEach((id) => fetchedIdsRef.current.delete(id));
      });
  }, [conversations, resolvedUserId, bulkSetPresence]);

  // ── Search filter ──────────────────────────────────────────────────────────
  const filtered = conversations.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    // Match on name, or member names for DMs
    if (c.name?.toLowerCase().includes(q)) return true;
    const members = c.members || [];
    return members.some((m) => {
      const name = m.full_name || m.user?.full_name || m.employee_id || "";
      return name.toLowerCase().includes(q);
    });
  });

  // ── Sort: most recently updated first ─────────────────────────────────────
  const sorted = [...filtered].sort((a, b) => {
    const ta = a.last_message?.created_at || a.updated_at || "";
    const tb = b.last_message?.created_at || b.updated_at || "";
    return tb.localeCompare(ta);
  });

  return (
    <div className="flex flex-col h-full">
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/8 shrink-0">
        <div className="flex items-center gap-2">
          <CurrentUserAvatar
            resolvedUserId={resolvedUserId}
            onShowProfile={setProfileTarget}
          />
          <div className="flex flex-col">
            <span className="text-[11px] font-bold text-slate-100 leading-tight">Messages</span>
            <button 
              onClick={togglePresence}
              disabled={updatingPresence || !wsConnected}
              className={`flex items-center gap-1.5 mt-0.5 rounded px-1 -ml-1 transition-colors ${
                !wsConnected ? "opacity-50 cursor-not-allowed" : "hover:bg-white/10 cursor-pointer"
              }`}
              title={isOnline ? "Set to Offline (Invisible)" : "Set to Online"}
            >
              {isOnline ? (
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_5px_rgba(16,185,129,0.5)]" />
              ) : (
                <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
              )}
              <span className="text-[9px] text-slate-400 font-medium uppercase tracking-wider flex items-center gap-0.5">
                {isOnline ? "Online" : "Offline"}
                {wsConnected && <FiChevronDown size={10} className="opacity-50" />}
              </span>
            </button>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={loadConversations}
            disabled={loading}
            className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-white/5 transition-all disabled:opacity-40"
            title="Refresh"
          >
            <FiRefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </button>
          <button
            onClick={() => setShowNewDM(true)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-white/5 transition-all"
            title="New Direct Message"
          >
            <FiEdit size={13} />
          </button>
          <button
            onClick={() => setShowNewGroup(true)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-white/5 transition-all"
            title="New Group"
          >
            <FiUsers size={13} />
          </button>
        </div>
      </div>

      {/* ── Search ── */}
      <div className="px-3 pt-2.5 pb-2 shrink-0">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/5 border border-white/8 focus-within:border-blue-500/40 transition-colors">
          <FiSearch size={12} className="text-slate-500 shrink-0" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 bg-transparent text-xs text-slate-300 placeholder-slate-600 outline-none"
          />
        </div>
      </div>

      {/* ── List ── */}
      <div className="flex-1 overflow-y-auto px-2 pb-4 space-y-0.5"
        style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,0.06) transparent" }}
      >
        {loading && conversations.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <FiLoader size={16} className="text-slate-500 animate-spin" />
            <span className="text-xs text-slate-600">Loading…</span>
          </div>
        )}

        {error && !loading && (
          <div className="flex flex-col items-center justify-center py-8 gap-2 px-4">
            <p className="text-xs text-red-400 text-center">{error}</p>
            <button
              onClick={loadConversations}
              className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
            >
              Try again
            </button>
          </div>
        )}

        {!loading && !error && sorted.length === 0 && (
          <div className="text-center py-12">
            <p className="text-xs text-slate-600">
              {search ? "No conversations match your search." : "No conversations yet."}
            </p>
          </div>
        )}

        {sorted.map((conv) => (
          <ConversationItem
            key={conv.id}
            conversation={conv}
            currentUserId={resolvedUserId}
          />
        ))}
      </div>

      {showNewDM && (
        <NewDMModal
          onClose={() => setShowNewDM(false)}
          currentUserId={resolvedUserId}
        />
      )}

      {showNewGroup && (
        <NewGroupModal
          onClose={() => setShowNewGroup(false)}
          currentUserId={resolvedUserId}
        />
      )}

      {profileTarget && (
        <UserProfileModal 
          user={profileTarget} 
          onClose={() => setProfileTarget(null)} 
        />
      )}
    </div>
  );
}